import React, { useState, useMemo } from 'react';
import { Table, Button, Form } from 'react-bootstrap';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const AnnualLeave: React.FC = () => {
  const { currentUser } = useAuth();

  // Define users who can modify annual leave
  const canModifyUsers = ['Jacky', 'Henry', 'Kenneth', 'Linda', 'Angela'];
  const canModify = canModifyUsers.includes(currentUser || '');

  // Define users who cannot see the annual leave data
  const noAccessUsers = ['Crystal', 'Nicole', 'Windy'];
  const hasNoAccess = noAccessUsers.includes(currentUser || '');

  // User-specific annual leave data based on the current user
  const getUserAnnualLeaveData = () => {
    if (hasNoAccess) {
      return []; // Return empty array for users with no access
    }

    // Default data for Harry Yip
    let baseData = [
      ['Harry Yip', '12', '2023/11/18'],
      ['Leavebank Applied Annual Leave', '25', 'day'],
      ['2024/8/18', '-1', 'day'],
      ['OT on 1/1/2025', '1', 'day'],
      ['2025/3/3', '-1', 'day'],
      ['As at today', '24', 'day']
    ];

    // Modify data based on current user
    if (currentUser) {
      baseData = baseData.map(row => {
        if (row[0] === 'Harry Yip') {
          return [currentUser, row[1], row[2]]; // Replace name with current user
        }
        return row;
      });
    }

    return baseData;
  };

  const [annualLeaveData, setAnnualLeaveData] = useState(getUserAnnualLeaveData());

  // State for selected rows
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const { t } = useLanguage();

  // Handle row selection
  const handleRowSelect = (index: number) => {
    if (selectedRows.includes(index)) {
      setSelectedRows(selectedRows.filter(i => i !== index));
    } else {
      setSelectedRows([...selectedRows, index]);
    }
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedRows.length === annualLeaveData.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(annualLeaveData.map((_, index) => index));
    }
  };

  // Delete selected rows
  const handleDeleteSelected = () => {
    if (selectedRows.length === 0) return;

    // Filter out rows that are selected (except "As at today")
    const newAnnualLeaveData = annualLeaveData.filter((_, index) => {
      // Don't delete the "As at today" row
      if (annualLeaveData[index][0] === 'As at today') return true;
      return !selectedRows.includes(index);
    });

    setAnnualLeaveData(newAnnualLeaveData);
    setSelectedRows([]);
  };

  // Add a new annual leave entry above "As at today"
  const handleAddAnnualLeave = () => {
    // Find the index of "As at today" row
    const asAtTodayIndex = annualLeaveData.findIndex(row => row[0] === 'As at today');

    if (asAtTodayIndex !== -1) {
      // Insert a new row above "As at today"
      const newAnnualLeaveData = [...annualLeaveData];
      newAnnualLeaveData.splice(asAtTodayIndex, 0, ['New Entry', '0', 'day']);
      setAnnualLeaveData(newAnnualLeaveData);
    } else {
      // If "As at today" doesn't exist, add to the end
      setAnnualLeaveData([...annualLeaveData, ['New Entry', '0', 'day']]);
    }
  };


  // Update "As at today" value when data changes
  const annualLeaveDataWithCalculated = useMemo(() => {
    if (hasNoAccess) return []; // Return empty array if user has no access

    let sum = 25; // Start with 25

    annualLeaveData.forEach((row, index) => {
      if (row[0] !== 'As at today' && row[0] !== currentUser && row[0] !== 'Leavebank Applied Annual Leave') {
        const value = parseInt(row[1]);
        if (!isNaN(value)) {
          sum += value;
        }
      }
    });

    return annualLeaveData.map((row: string[]) => {
      if (row[0] === 'As at today') {
        return ['As at today', sum.toString(), 'day'];
      }
      return row;
    });
  }, [annualLeaveData, hasNoAccess, currentUser]);

  // If user has no access, show a message
  if (hasNoAccess) {
    return (
      <div className="annual-leave">
        <h2>{t('annualLeave')}</h2>
        <div className="alert alert-info">
          {t('noAnnualLeaveAccess')}
        </div>
      </div>
    );
  }

  return (
    <div className="annual-leave">
      <h2>{t('annualLeave')}</h2>

      <div className="d-flex justify-content-between mb-3">
        <div>
          <Button
            variant="primary"
            className="me-2"
            onClick={handleAddAnnualLeave}
            disabled={!canModify} // Disable if user cannot modify
          >
            {t('addAnnualLeave')}
          </Button>
          <Button
            variant="danger"
            disabled={selectedRows.length === 0 || !canModify} // Disable if user cannot modify
            onClick={handleDeleteSelected}
          >
            {t('deleteAnnualLeave')}
          </Button>
        </div>
      </div>

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th></th> {/* Checkbox column header */}
            <th>
              <div>{t('description')}</div>
              <div className="text-muted small">(描述)</div>
            </th>
            <th>
              <div>{t('value')}</div>
              <div className="text-muted small">(數值)</div>
            </th>
            <th>
              <div>{t('Joining date')}</div>
              <div className="text-muted small">(入職時間)</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {annualLeaveDataWithCalculated.map((row: string[], index: number) => (
            <tr key={index}>
              <td><Form.Check
                  type="checkbox"
                  checked={selectedRows.includes(index)}
                  onChange={() => handleRowSelect(index)}
                  disabled={row[0] === 'As at today' || !canModify} // Disable checkbox for "As at today" row or if user can't modify
                /></td>
              <td><strong>{row[0]}</strong></td>
              <td>{row[1]}</td>
              <td>{row[2]}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default AnnualLeave;